import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api, { markSession } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = user
  const [loading, setLoading] = useState(true);
  const [sessionExpiresAt, setSessionExpiresAt] = useState(null); // ISO string from the server

  const applySession = useCallback((data) => {
    markSession(true);
    if (data?.session_expires_at) setSessionExpiresAt(data.session_expires_at);
  }, []);

  useEffect(() => {
    // If returning from Google OAuth, AuthCallback exchanges the session_id first.
    if (window.location.hash?.includes("session_id=")) {
      setUser(false);
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        applySession(data);
      } catch {
        markSession(false);
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [applySession]);

  // Refresh cookie gone too (signed out elsewhere / expired) — drop to the login screen instead of a dead page.
  useEffect(() => {
    const onLost = () => { setSessionExpiresAt(null); setUser((u) => (u && u.id ? false : u)); };
    window.addEventListener("mfg:session-lost", onLost);
    return () => window.removeEventListener("mfg:session-lost", onLost);
  }, []);

  // Coming back to a tab after hours away: re-check the session right away so the first click never hits a dead page.
  useEffect(() => {
    const onVisible = async () => {
      if (document.visibilityState !== "visible") return;
      try { const { data } = await api.get("/auth/me"); setUser(data); applySession(data); } catch { /* interceptor handled it */ }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [applySession]);

  // api.js refreshes silently on 401 — keep the expiry clock in sync.
  useEffect(() => {
    const onRefresh = (e) => applySession(e.detail);
    window.addEventListener("mfg:session-refreshed", onRefresh);
    return () => window.removeEventListener("mfg:session-refreshed", onRefresh);
  }, [applySession]);

  const login = async (email, password, rememberDays = 0) => {
    const { data } = await api.post("/auth/login", { email, password, remember: !!rememberDays, remember_days: rememberDays || 0 });
    setUser(data);
    applySession(data);
    return data;
  };

  // Session already minted server-side (e.g. passkey sign-in) — adopt it.
  const applyLogin = (data) => { setUser(data); applySession(data); };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    applySession(data);
    return data;
  };

  const extendSession = useCallback(async () => {
    const { data } = await api.post("/auth/refresh");
    applySession(data);
    return data;
  }, [applySession]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) {
      // Server-side logout is best-effort — token is cleared locally regardless.
      console.warn("[auth] server logout failed, clearing local session:", err?.message || err);
    }
    markSession(false);
    setSessionExpiresAt(null);
    setUser(false);
  };

  const patchUser = (fields) => setUser((u) => (u && u.id ? { ...u, ...fields } : u));

  return (
    <AuthContext.Provider value={{ user, loading, login, applyLogin, register, logout, sessionExpiresAt, extendSession, patchUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
